// Shared auth state for every API client (core api.js, chatapi.js, iamApi.js).
//
// The core API now requires authentication and scopes all data to the caller's
// active org (SAAS.3.1). Every request therefore carries the bearer token and an
// `X-Org-Id` header. The AuthContext is the single writer (token from the Supabase
// session / dev login; active org from the org switcher); the API clients are
// readers via the `attachAuth` axios request interceptor.
let _token = null;
let _activeOrg = null;
let _activeProject = null;

export function setAuthToken(t) {
  _token = t || null;
}

export function getAuthToken() {
  return _token;
}

export function setActiveOrg(orgId) {
  _activeOrg = orgId || null;
}

export function getActiveOrg() {
  return _activeOrg;
}

export function setActiveProject(projectId) {
  _activeProject = projectId || null;
}

export function getActiveProject() {
  return _activeProject;
}

// #917: a deep link names an ITEM and says nothing about its project, so that item's own
// requests would carry the viewer's stored selection and 404 when the two disagree. These
// record the project an item has been resolved to — `openToItem` learns it from
// getAncestors — so the item's requests scope themselves.
//
// This is deliberately NON-persistent and viewer-neutral. It is a module-level map, never
// written to localStorage, and it does not touch `_activeProject`. An earlier attempt on
// this defect called chooseProject() instead: the switch outlived the deep link, every
// later request in the session carried the new project, and items in the project the
// viewer had been moved out of started 404ing. That attempt was reverted; see #917.
//
// Bounded so a long session walking many cross-project links cannot grow it without limit.
const ITEM_PROJECT_LIMIT = 200;
const _itemProjects = new Map();

export function noteItemProject(itemId, projectId) {
  if (!itemId || !projectId) return;
  // Re-inserting moves the key to the end, so the eviction below is least-recently-noted.
  _itemProjects.delete(itemId);
  _itemProjects.set(itemId, projectId);
  while (_itemProjects.size > ITEM_PROJECT_LIMIT) {
    _itemProjects.delete(_itemProjects.keys().next().value);
  }
}

export function getItemProject(itemId) {
  return (itemId && _itemProjects.get(itemId)) || null;
}

export function clearItemProjects() {
  _itemProjects.clear();
}

// The item id in an item-scoped path: /items/<id>, /items/<id>/history,
// /items/<id>/comments/count, /items/<id>/breakdown, /items/<id>/approval-trail.
// Anchored on `/items/` so a path that merely contains the word does not match, and the
// id segment must be non-empty — `/items//history` (an unresolved id) yields no match.
const ITEM_PATH = /(?:^|\/)items\/([^/]+)/;

export function itemIdFromUrl(url) {
  if (!url) return null;
  // Match the PATH only. Scanning the whole URL would let a query string that happens to
  // contain `items/<something>` (a search term, a redirect target) scope an unrelated
  // request to that id's project — silently wrong, and only for users who had noted it.
  const path = String(url).split(/[?#]/)[0];
  const m = ITEM_PATH.exec(path);
  return m ? decodeURIComponent(m[1]) : null;
}

// axios request interceptor: attach the bearer token + active-org / active-project headers.
// X-Project-Id is what scopes reads + stamps writes to the selected project (#125): without
// it the backend can't know which project the user picked, so writes would land outside any
// project. The AuthContext is the single writer of the active project (the project switcher).
export function attachAuth(config) {
  if (_token) config.headers.Authorization = `Bearer ${_token}`;
  if (_activeOrg) config.headers['X-Org-Id'] = _activeOrg;
  // A per-call X-Project-Id (e.g. the lazy tree fetching each visible project's level)
  // wins over the global active project, so multi-project views can scope each request
  // independently. Only fall back to the global active project when none was set.
  // #917: a request that must NOT be scoped to the viewer's selected project. Used to
  // discover which project an item belongs to, which is unanswerable from inside that
  // scope: the backend resolves /items/:id/ancestors through the tenancy-scoped manager
  // (api.py get_ancestors), so for an item outside the selection it returns an empty
  // ancestor list and a null project_id. Not an authz widening — this is the request the
  // app already sends whenever no project is selected (null = all projects), and org
  // scoping plus RLS still apply.
  if (config.rmdvUnscopedProject) {
    delete config.headers['X-Project-Id'];
  } else if (!config.headers['X-Project-Id']) {
    // #917: an item whose project has been resolved scopes its own requests to THAT
    // project, ahead of the viewer's stored selection, so a cross-project deep link
    // loads instead of 404ing. Nothing is persisted and the stored selection is
    // unchanged — only this request is scoped differently.
    const noted = getItemProject(itemIdFromUrl(config.url));
    if (noted) config.headers['X-Project-Id'] = noted;
    else if (_activeProject) config.headers['X-Project-Id'] = _activeProject;
  }
  // #517: a lightweight, NON-authoritative marker so the REST edge can tell "this
  // request came from the SPA" apart from any external/programmatic API caller (which
  // authenticates with an identical Supabase bearer JWT and has no other distinguishing
  // claim). Every request through the shared api.js/chatapi.js/iamApi.js/billingApi.js/
  // notificationsApi.js clients carries it, since they all funnel through this single
  // interceptor. It only ever affects a history/analytics display label
  // (web.backend.auth.rest_source) — never authorization, so there's no security
  // reliance on a header a caller could trivially spoof.
  config.headers['X-Client'] = 'rmdv-web';
  return config;
}
